/**
 * SA: YouTube Card
 * Custom element for video carousel with center-loop, card info sync, and inline playback.
 */
if (!customElements.get('sa-youtube-card')) {
  customElements.define(
    'sa-youtube-card',
    class SaYoutubeCard extends HTMLElement {
      constructor() {
        super();
        this.boundOnClick = this.onClick.bind(this);
        this.boundOnKeydown = this.onKeydown.bind(this);
        this.carouselSwiper = null;
        this.carouselLoopHandler = null;
        this.carouselPauseHandler = null;
        this.carouselProgressHandler = null;
        this.carouselProgressSwiper = null;
        this.carouselProgressCarousel = null;
        this.carouselLoopTimer = null;
        this.carouselReinitTimer = null;
        this.carouselBreakpointQuery = null;
        this.boundCarouselBreakpointChange = this.onCarouselBreakpointChange.bind(this);
        this.autoplayPausedByVideo = false;
      }

      connectedCallback() {
        this.removeEventListener('click', this.boundOnClick);
        this.addEventListener('click', this.boundOnClick);
        this.removeEventListener('keydown', this.boundOnKeydown);
        this.addEventListener('keydown', this.boundOnKeydown);
        this.setupCarouselBreakpointWatcher();
        this.initCarouselLoop();
        this.syncActiveCardInfo();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.boundOnClick);
        this.removeEventListener('keydown', this.boundOnKeydown);
        this.teardownCarouselBreakpointWatcher();
        this.teardownCarouselLoop();
      }

      setupCarouselBreakpointWatcher() {
        const carousel = this.querySelector('.sa-youtube-card__swiper[data-center-loop-min-width]');
        if (!carousel || this.carouselBreakpointQuery) return;

        const minWidth = parseInt(carousel.dataset.centerLoopMinWidth || '0', 10);
        if (!minWidth || !window.matchMedia) return;

        this.carouselBreakpointQuery = window.matchMedia('(min-width: ' + minWidth + 'px)');
        if (typeof this.carouselBreakpointQuery.addEventListener === 'function') {
          this.carouselBreakpointQuery.addEventListener('change', this.boundCarouselBreakpointChange);
        } else if (typeof this.carouselBreakpointQuery.addListener === 'function') {
          this.carouselBreakpointQuery.addListener(this.boundCarouselBreakpointChange);
        }
      }

      teardownCarouselBreakpointWatcher() {
        if (this.carouselReinitTimer) {
          clearTimeout(this.carouselReinitTimer);
          this.carouselReinitTimer = null;
        }
        if (this.carouselBreakpointQuery) {
          if (typeof this.carouselBreakpointQuery.removeEventListener === 'function') {
            this.carouselBreakpointQuery.removeEventListener('change', this.boundCarouselBreakpointChange);
          } else if (typeof this.carouselBreakpointQuery.removeListener === 'function') {
            this.carouselBreakpointQuery.removeListener(this.boundCarouselBreakpointChange);
          }
          this.carouselBreakpointQuery = null;
        }
      }

      onCarouselBreakpointChange() {
        if (this.carouselReinitTimer) clearTimeout(this.carouselReinitTimer);
        this.carouselReinitTimer = setTimeout(() => {
          this.reinitCarouselForBreakpoint();
        }, 120);
      }

      reinitCarouselForBreakpoint() {
        const carousel = this.querySelector('.sa-youtube-card__swiper');
        if (!carousel || typeof carousel.initSlide !== 'function') return;

        this.pauseInactiveCarouselMedia();
        this.teardownCarouselLoop();

        if (carousel.globalSlide && typeof carousel.globalSlide.destroy === 'function' && !carousel.globalSlide.destroyed) {
          carousel.globalSlide.destroy(true, true);
        }
        carousel.globalSlide = null;
        carousel.querySelectorAll('.sa-youtube-card__slide.cm-clone').forEach((slide) => slide.remove());

        carousel.initSlide();
        this.initCarouselLoop();
        this.syncActiveCardInfo();
      }

      initCarouselLoop() {
        const carousel = this.querySelector('.sa-youtube-card__swiper[data-center-loop="true"]');
        if (!carousel) {
          this.syncActiveCardInfo();
          return;
        }
        const loopMinWidth = parseInt(carousel.dataset.centerLoopMinWidth || '0', 10);
        if (loopMinWidth && !window.matchMedia('(min-width: ' + loopMinWidth + 'px)').matches) {
          this.syncActiveCardInfo();
          this._initMobileSlideSync(carousel);
          return;
        }

        this.clearCarouselLoopTimer();
        var attempts = 0;
        var self = this;
        var bindLoop = function() {
          attempts += 1;
          var swiper = carousel.globalSlide;
          if (!swiper) {
            if (attempts < 60) self.carouselLoopTimer = setTimeout(bindLoop, 100);
            return;
          }

          self.clearCarouselLoopTimer();
          self.teardownCarouselLoop();
          var cloneCount = parseInt(carousel.dataset.centerClones || '2', 10);
          var realCount = carousel.querySelectorAll('.sa-youtube-card__slide:not(.cm-clone)').length;
          if (!cloneCount || realCount <= 1) {
            self.carouselSwiper = swiper;
            self.bindCarouselProgress(swiper, carousel);
            self.syncActiveCardInfo();
            return;
          }

          self.carouselSwiper = swiper;
          self.carouselLoopHandler = function() {
            if (swiper.destroyed) return;
            if (swiper.activeIndex < cloneCount) {
              swiper.slideTo(swiper.activeIndex + realCount, 0, false);
            } else if (swiper.activeIndex >= cloneCount + realCount) {
              swiper.slideTo(swiper.activeIndex - realCount, 0, false);
            }
            self.pauseInactiveCarouselMedia();
            self.syncActiveCardInfo();
            self.syncCarouselProgress(swiper, carousel);
          };
          self.carouselPauseHandler = function() {
            self.pauseInactiveCarouselMedia();
            self.syncActiveCardInfo();
            self.syncCarouselProgress(swiper, carousel);
          };

          swiper.on('slideChangeTransitionStart', self.carouselPauseHandler);
          swiper.on('slideChange', self.carouselPauseHandler);
          swiper.on('transitionEnd', self.carouselLoopHandler);
          self.bindCarouselProgress(swiper, carousel);
          self.carouselLoopHandler();
        };

        bindLoop();
      }

      syncActiveCardInfo() {
        var infoRoot = this.querySelector('[data-sa-card-info]');
        if (!infoRoot) return;

        var activeSlide = this.querySelector('.sa-youtube-card__slide.swiper-slide-active') || this.querySelector('.sa-youtube-card__slide');
        if (!activeSlide) return;

        var title = activeSlide.dataset.cardTitle || '';
        var author = activeSlide.dataset.cardAuthor || '';
        var avatar = activeSlide.dataset.cardAvatar || '';
        var avatarAlt = activeSlide.dataset.cardAvatarAlt || '';

        var logo = infoRoot.querySelector('[data-sa-card-info-logo]');
        var titleEl = infoRoot.querySelector('[data-sa-card-info-title]');
        var authorEl = infoRoot.querySelector('[data-sa-card-info-author]');

        if (logo) {
          if (avatar) {
            logo.src = avatar;
            logo.alt = avatarAlt;
            logo.hidden = false;
          } else {
            logo.hidden = true;
            logo.removeAttribute('src');
            logo.alt = '';
          }
        }

        if (titleEl) {
          titleEl.textContent = title;
          titleEl.hidden = title.length === 0;
        }

        if (authorEl) {
          authorEl.textContent = author;
          authorEl.hidden = author.length === 0;
        }
      }

      teardownCarouselLoop() {
        this.clearCarouselLoopTimer();
        if (this._mobileSyncTimer) {
          clearTimeout(this._mobileSyncTimer);
          this._mobileSyncTimer = null;
        }
        if (this._mobileSyncSwiper && this._mobileSyncHandler) {
          this._mobileSyncSwiper.off('slideChange', this._mobileSyncHandler);
          this._mobileSyncSwiper.off('slideChangeTransitionEnd', this._mobileSyncHandler);
          this._mobileSyncSwiper = null;
          this._mobileSyncHandler = null;
        }
        if (this.carouselProgressSwiper && this.carouselProgressHandler) {
          this.carouselProgressSwiper.off('init', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('slideChange', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('slideChangeTransitionStart', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('activeIndexChange', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('transitionEnd', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('resize', this.carouselProgressHandler);
        }
        if (this.carouselSwiper && this.carouselPauseHandler && typeof this.carouselSwiper.off === 'function') {
          this.carouselSwiper.off('slideChangeTransitionStart', this.carouselPauseHandler);
          this.carouselSwiper.off('slideChange', this.carouselPauseHandler);
        }
        if (this.carouselSwiper && this.carouselLoopHandler && typeof this.carouselSwiper.off === 'function') {
          this.carouselSwiper.off('transitionEnd', this.carouselLoopHandler);
        }
        this.carouselSwiper = null;
        this.carouselLoopHandler = null;
        this.carouselPauseHandler = null;
        this.carouselProgressHandler = null;
        this.carouselProgressSwiper = null;
        this.carouselProgressCarousel = null;
      }

      clearCarouselLoopTimer() {
        if (this.carouselLoopTimer) {
          clearTimeout(this.carouselLoopTimer);
          this.carouselLoopTimer = null;
        }
      }

      _initMobileSlideSync(carousel) {
        var self = this;
        var attempts = 0;
        this._mobileSyncTimer = null;
        var bindSync = function() {
          attempts += 1;
          var swiper = carousel.globalSlide;
          if (!swiper) {
            if (attempts < 60) self._mobileSyncTimer = setTimeout(bindSync, 100);
            return;
          }
          self._mobileSyncTimer = null;
          self._mobileSyncSwiper = swiper;
          self._mobileSyncHandler = function() {
            self.syncActiveCardInfo();
            self.syncCarouselProgress(swiper, carousel);
          };
          swiper.on('slideChange', self._mobileSyncHandler);
          swiper.on('slideChangeTransitionEnd', self._mobileSyncHandler);
          self.bindCarouselProgress(swiper, carousel);
          // Sync initial state
          self.syncActiveCardInfo();
          self.syncCarouselProgress(swiper, carousel);
        };
        bindSync();
      }

      bindCarouselProgress(swiper, carousel) {
        if (!swiper || !carousel) return;
        if (this.carouselProgressSwiper && this.carouselProgressHandler) {
          this.carouselProgressSwiper.off('init', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('slideChange', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('slideChangeTransitionStart', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('activeIndexChange', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('transitionEnd', this.carouselProgressHandler);
          this.carouselProgressSwiper.off('resize', this.carouselProgressHandler);
        }

        var self = this;
        this.carouselProgressSwiper = swiper;
        this.carouselProgressCarousel = carousel;
        this.carouselProgressHandler = function() {
          self.syncCarouselProgress(swiper, carousel);
        };

        swiper.on('init', this.carouselProgressHandler);
        swiper.on('slideChange', this.carouselProgressHandler);
        swiper.on('slideChangeTransitionStart', this.carouselProgressHandler);
        swiper.on('activeIndexChange', this.carouselProgressHandler);
        swiper.on('transitionEnd', this.carouselProgressHandler);
        swiper.on('resize', this.carouselProgressHandler);
        this.syncCarouselProgress(swiper, carousel);
      }

      getCarouselRealIndex(carousel, swiper) {
        var realCount = carousel.querySelectorAll('.sa-youtube-card__slide:not(.cm-clone)').length;
        if (realCount <= 0) return 0;

        var cloneCount = parseInt(carousel.dataset.centerClones || '2', 10);
        var loopMinWidth = parseInt(carousel.dataset.centerLoopMinWidth || '0', 10);
        var centerLoopActive =
          carousel.dataset.centerLoop === 'true' &&
          cloneCount > 0 &&
          realCount > 1 &&
          (!loopMinWidth || window.matchMedia('(min-width: ' + loopMinWidth + 'px)').matches);

        if (!centerLoopActive) {
          var activeIndex = typeof swiper.activeIndex === 'number' ? swiper.activeIndex : 0;
          return Math.min(Math.max(activeIndex, 0), realCount - 1);
        }

        var realIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex - cloneCount;
        realIndex = ((realIndex % realCount) + realCount) % realCount;
        return realIndex;
      }

      syncCarouselProgress(swiper, carousel) {
        var fill = this.querySelector('.sa-youtube-card__controls .swiper-pagination-progressbar-fill');
        if (!fill || !swiper || !carousel) return;

        var realSlides = carousel.querySelectorAll('.sa-youtube-card__slide:not(.cm-clone)');
        var realCount = realSlides.length;
        if (!realCount) return;

        var realIndex = this.getCarouselRealIndex(carousel, swiper);
        var progress = realCount === 1 ? 1 : realIndex / (realCount - 1);
        fill.style.width = '100%';
        fill.style.transformOrigin = 'left center';
        fill.style.transform = 'scaleX(' + progress + ')';
      }

      getCarouselSwiper() {
        var carousel = this.querySelector('.sa-youtube-card__swiper');
        return carousel && carousel.globalSlide ? carousel.globalSlide : this.carouselSwiper || this._mobileSyncSwiper;
      }

      pauseCarouselAutoplay() {
        var swiper = this.getCarouselSwiper();
        if (!swiper || !swiper.autoplay || typeof swiper.autoplay.stop !== 'function') return;
        swiper.autoplay.stop();
        this.autoplayPausedByVideo = true;
      }

      resumeCarouselAutoplayIfIdle() {
        if (!this.autoplayPausedByVideo) return;
        if (this.querySelector('.sa-youtube-card__media.is-playing')) return;

        var carousel = this.querySelector('.sa-youtube-card__swiper');
        if (!carousel || carousel.dataset.autoplay !== 'true') {
          this.autoplayPausedByVideo = false;
          return;
        }

        var swiper = this.getCarouselSwiper();
        if (swiper && swiper.autoplay && typeof swiper.autoplay.start === 'function') {
          swiper.autoplay.start();
        }
        this.autoplayPausedByVideo = false;
      }

      resolveVideoCardFromEventTarget(target) {
        if (!target) return null;
        var card = target.closest('[data-sa-video-card]');
        if (card) return card;
        var playTrigger = target.closest('[data-sa-video-trigger]');
        return playTrigger ? playTrigger.closest('[data-sa-video-card]') : null;
      }

      onClick(event) {
        var card = this.resolveVideoCardFromEventTarget(event.target);
        if (!card || !this.contains(card)) return;
        this.toggleVideoFromTrigger(card);
      }

      onKeydown(event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        var card = this.resolveVideoCardFromEventTarget(event.target);
        if (!card || !this.contains(card)) return;
        event.preventDefault();
        this.toggleVideoFromTrigger(card);
      }

      toggleVideoFromTrigger(trigger) {
        var card = trigger.closest ? trigger.closest('[data-sa-video-card]') || trigger : trigger;
        if (!card || !card.matches('[data-sa-video-card]')) return;

        var carouselSlide = card.closest('.sa-youtube-card__slide');
        if (carouselSlide && !carouselSlide.classList.contains('swiper-slide-active')) return;

        var player = card.querySelector('[data-sa-video-player]');
        if (!player) return;

        if (card.classList.contains('is-playing')) {
          this.pauseMediaCard(card);
        } else {
          this.playMediaCard(card);
        }
      }

      playMediaCard(card) {
        var player = card.querySelector('[data-sa-video-player]');
        if (!player) return;
        var self = this;

        var video = player.querySelector('video');
        if (video) {
          if (!video.dataset.saTutorialReady) {
            var playableVideo = video.cloneNode(true);
            playableVideo.dataset.saTutorialReady = 'true';
            video.replaceWith(playableVideo);
            video = playableVideo;
          }
          var source = video.dataset.src;
          if (source && !video.getAttribute('src')) {
            video.setAttribute('src', source);
            video.removeAttribute('data-src');
            video.load();
          }
          video.controls = false;
          video.muted = false;
          card.classList.add('is-playing');
          this.pauseCarouselAutoplay();
          if (!video.dataset.saTutorialPauseGuard) {
            video.dataset.saTutorialPauseGuard = 'true';
            video.addEventListener('pause', function() { self.resumePlayingVideo(card, video); });
          }
          video.addEventListener('ended', function() {
            card.classList.remove('is-playing');
            self.resumeCarouselAutoplayIfIdle();
          }, { once: true });
          video.play().catch(function() {
            card.classList.remove('is-playing');
            self.resumeCarouselAutoplayIfIdle();
          });
          return;
        }

        var iframe = player.querySelector('iframe');
        if (iframe) {
          var base = iframe.dataset.embedSrc || '';
          if (!base) return;

          var hasLoadedSrc = Boolean(iframe.getAttribute('src'));
          if (!hasLoadedSrc) {
            var playbackSrc = this.buildPlaybackEmbedSrc(base);
            var startSeconds = 0;
            try {
              startSeconds = parseInt(new URL(playbackSrc, window.location.href).searchParams.get('start') || '0', 10);
            } catch (error) {
              startSeconds = 0;
            }
            if (startSeconds > 0) {
              iframe.addEventListener(
                'load',
                function() {
                  setTimeout(function() {
                    self.postToIframeSeek(iframe, startSeconds);
                  }, 300);
                },
                { once: true }
              );
            }
            iframe.src = playbackSrc;
          } else {
            this.postToIframe(iframe, 'play');
          }
          card.classList.add('is-playing');
          this.pauseCarouselAutoplay();
        }
      }

      buildPlaybackEmbedSrc(base) {
        try {
          var url = new URL(base, window.location.href);
          var isYoutube = url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be');
          var isVimeo = url.hostname.includes('vimeo.com');

          if (isYoutube) {
            url.searchParams.set('autoplay', '1');
            url.searchParams.set('playsinline', '1');
            url.searchParams.set('controls', '1');
            url.searchParams.set('rel', '0');
            url.searchParams.set('enablejsapi', '1');
            url.searchParams.set('mute', '0');
            url.searchParams.set('origin', window.location.origin);
          } else if (isVimeo) {
            url.searchParams.set('autoplay', '1');
            url.searchParams.set('playsinline', '1');
            url.searchParams.set('controls', '1');
            url.searchParams.set('api', '1');
            url.searchParams.set('muted', '0');
          }

          return url.toString();
        } catch (error) {
          var origin = encodeURIComponent(window.location.origin);
          var joiner = base.indexOf('?') === -1 ? '?' : '&';
          return base + joiner + 'autoplay=1&playsinline=1&controls=1&enablejsapi=1&mute=0&origin=' + origin;
        }
      }

      resumePlayingVideo(card, video) {
        if (!card.classList.contains('is-playing') || video.ended) return;
        var self = this;
        setTimeout(function() {
          if (!card.classList.contains('is-playing') || !video.paused || video.ended) return;
          video.play().catch(function() { card.classList.remove('is-playing'); });
        }, 50);
      }

      postToIframe(iframe, action) {
        if (!iframe || !iframe.contentWindow) return;
        var src = iframe.src || '';
        var isYt = src.includes('youtube.com') || src.includes('youtu.be');
        var ytOrigin = src.includes('youtube-nocookie.com') ? 'https://www.youtube-nocookie.com' : 'https://www.youtube.com';
        var vimeoOrigin = 'https://player.vimeo.com';

        if (action === 'play') {
          if (isYt) {
            iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', ytOrigin);
          } else {
            iframe.contentWindow.postMessage('{"method":"play"}', vimeoOrigin);
          }
          return;
        }
        if (isYt) {
          iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', ytOrigin);
        } else {
          iframe.contentWindow.postMessage('{"method":"pause"}', vimeoOrigin);
        }
      }

      postToIframeSeek(iframe, seconds) {
        if (!iframe || !iframe.contentWindow) return;
        var src = iframe.src || '';
        if (!src.includes('youtube.com') && !src.includes('youtu.be')) return;
        var ytOrigin = src.includes('youtube-nocookie.com') ? 'https://www.youtube-nocookie.com' : 'https://www.youtube.com';
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"seekTo","args":[' + seconds + ',true]}',
          ytOrigin
        );
      }

      pauseMediaCard(card) {
        card.classList.remove('is-playing');
        var video = card.querySelector('video');
        if (video) video.pause();
        var iframe = card.querySelector('iframe');
        if (iframe) {
          this.postToIframe(iframe, 'pause');
          iframe.removeAttribute('src');
        }
        this.resumeCarouselAutoplayIfIdle();
      }

      pauseInactiveCarouselMedia() {
        var self = this;
        this.querySelectorAll('.sa-youtube-card__slide').forEach(function(slide) {
          if (slide.classList.contains('swiper-slide-active')) return;
          slide.querySelectorAll('[data-sa-video-card]').forEach(function(card) {
            self.pauseMediaCard(card);
          });
        });
      }
    }
  );
}
